import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';

import { Observable, Subscription, catchError, combineLatest, filter, map, of, throwError } from 'rxjs';

import { ChartLoadingOptionsEnum } from '../../enums/chart-loading-options.enum';
import { ChartModel } from '../../models/chart.model';
import { ReportsSeriesDataMergerService } from '../../../Reports/services/reports-series-data-merger.service';
import {
    newDataProcessingWorkflowBlockTypes,
    newDataProcessingWorkflowChartModelTypes
} from '../../../../Common/Reports/models/new-data-processing-workflow-block-types.model';


@Component({
    selector: 'agpr-chart',
    templateUrl: './chart.component.html',
    styleUrls: ['./chart.component.scss'],
    standalone: false
})
export class ChartComponent implements AfterViewInit, OnDestroy {
    @Input() id: string = null;
    @Input() smallIndicator: boolean = false;
    @Input() showLoadingUntil: ChartLoadingOptionsEnum = ChartLoadingOptionsEnum.all;
    @Input()
    set chart(chart: ChartModel) {
        if (this._chart && this._chart !== chart) {
            this.destroyOldChart();
        }
        this._chart = chart;
        this.initializeChart();
        this.chartId = chart?.metadata?.id;
    }
    get chart() {
        return this._chart;
    }
    @Output() noData: EventEmitter<boolean> = new EventEmitter(false); // this is used in backend error case
    @Output() noContent: EventEmitter<boolean> = new EventEmitter(); // this is used when backend returns zero results

    protected _chart: ChartModel;
    chartId: string;
    initFrame: number;
    viewInitted: boolean = false;
    loading: boolean = false;

    dataSubscriptions: Subscription[] = [];
    loadingSubscriber: Subscription;

    @ViewChild('chartElement', { static: true }) chartElement: ElementRef;

    constructor(
        public reportsSeriesDataMergerService: ReportsSeriesDataMergerService,
        public zone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngAfterViewInit() {
        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            this.viewInitted = true;
            this.initializeChart();
        });
    }

    ngOnDestroy() {
        cancelAnimationFrame(this.initFrame);
        this.destroyOldChart();
    }

    /**
     * Removes the old chart from the DOM, then waits 1 frame before calling destroy.
     * This is faster because when amcharts is destroying a chart, it does a lot of DOM manipulation
     * and it is a lot faster if it's not part of the document.
     */
    destroyOldChart() {
        // Remove watches for previous series
        this.unwatchLoading();
        this._chart?.destroy();
        this.dataSubscriptions.forEach((value) => {
            value.unsubscribe();
        });
        this.dataSubscriptions = [];
    }

    public initializeChart(): void {
        if (!this.viewInitted) {
            return;
        }

        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            if (this._chart) {
                // eslint-disable-next-line max-len
                // HACK: for reports drilldown, whenever we change the source we have to create a new chart, and it always adds the new chart instead of replacing it
                while (this.chartElement.nativeElement.firstChild) {
                    this.chartElement.nativeElement.removeChild(this.chartElement.nativeElement.lastChild);
                }
                this.zone.runOutsideAngular(() => {
                    this._chart.setZone(this.zone);
                    this._chart.initChart(this.chartElement.nativeElement, this._chart.chartType);
                    this.watchData(this._chart);
                    this.watchLoading();

                    // @HACK - the map chart fails to correctly ascertain the available space it has
                    // for the main container upon init. Making a change to the container's width
                    // AFTER init seems to fix this.
                    this._chart.amchart.htmlContainer.style.width = '100%';
                    this._chart.amchart.svgContainer.htmlElement.firstElementChild.setAttribute(
                        'style', 'width: 100%; height: 100%; position: relative; z-index: 500;'
                    );
                });
            }
        });
    }

    /**
     * Gets data from the series to display into the chart
     */
    public watchData(chart: ChartModel): void {
        const data = chart.series.map(item => null);
        const previousData = chart.series.map(item => null);
        const erroredSeries = chart.series.map(() => false);

        const useNewDataMappingWorkflow = newDataProcessingWorkflowBlockTypes.includes(chart.metadata?.type)
            && newDataProcessingWorkflowChartModelTypes.some(x => chart instanceof x);

        chart.series.forEach((item, index) => {
            let previousPeriodDataProvider$: Observable<any>;
            const currentPeriodDataProvider$: Observable<any> = item.dataProvider.pipe(
                filter(v => !!v),
                map(itemData => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = itemData;
                        if (chart.overrideNoContent) {
                            // sometimes this is needed because there is no previousDataSeries but existin in useNewDataMappingWorkflow
                            if (chart.isContentEmpty && chart.isContentEmpty()) {
                                this.noContent.emit(true);
                            } else {
                                this.noContent.emit(false);
                            }
                        }
                        if (useNewDataMappingWorkflow) {
                            return of(data);
                        } else {
                            chart.updateData(data.slice(), false);
                            if (chart.isContentEmpty && chart.isContentEmpty()) {
                                this.noContent.emit(true);
                            } else {
                                this.noContent.emit(false);
                            }
                            return of(null);
                        }
                    });
                }),
                catchError(err => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                            chart.amchart?.raiseCriticalError(err);
                        }
                    });
                    return throwError(() => new Error(err));
                })
            );
            if (item.hasPreviousPeriod) {
                previousPeriodDataProvider$ = item.previousPeriodDataProvider.pipe(
                    filter(v => !!v),
                    map((itemData) => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = itemData;
                            if (useNewDataMappingWorkflow) {
                                return of(previousData);
                            } else {
                                chart.updateData(previousData.slice(), true);
                                if (chart.isContentEmpty && chart.isContentEmpty()) {
                                    this.noContent.emit(true);
                                } else {
                                    this.noContent.emit(false);
                                }
                                return of(null);
                            }

                        });
                    }),
                    catchError(err => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = [];
                            erroredSeries[index] = true;
                            if (!erroredSeries.includes(false)) {
                                this.noData.emit(true);
                                chart.amchart?.raiseCriticalError(err);
                            }
                        });
                        return throwError(() => new Error(err));
                    })
                );
            } else {
                previousPeriodDataProvider$ = of([]);
            }
            combineLatest([
                currentPeriodDataProvider$,
                previousPeriodDataProvider$
            ]).subscribe({
                next: ([currentPeriodData, previousPeriodData]) => {
                    if (
                        useNewDataMappingWorkflow
                        && data.every(x => !!x)
                        && (!item.hasPreviousPeriod || previousData.every(x => !!x))
                    ) {
                        const mergedData = this.reportsSeriesDataMergerService.mergeSeriesData(
                            [item.hasPreviousPeriod ? previousData : [], data].flat(),
                            chart
                        );
                        chart.updateData(mergedData.slice(), true);
                        if (chart.isContentEmpty && chart.isContentEmpty()) {
                            this.noContent.emit(true);
                        } else {
                            this.noContent.emit(false);
                        }
                    }
                },
                error: (error) => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        previousData[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                            chart.amchart?.raiseCriticalError(error);
                        }
                    });
                }
            });
        });
    }

    /**
     * Start watching the loading state of series data
     */
    public watchLoading(): void {
        if (!this.chart || !this.chart.series?.length) {
            this.loading = false;
            return;
        }

        const seriesProviders = this.chart.series.map((seriesItem) => {
            return seriesItem.dataProvider;
        });

        if (this.showLoadingUntil === ChartLoadingOptionsEnum.all) {
            this.loading = true;
            this.loadingSubscriber = combineLatest(seriesProviders).subscribe({
                next: (data) => {
                    this.loading = false;
                    const stillLoadingItem = data.find((item) => {
                        if (!item) {
                            this.loading = true;
                            return true;
                        }
                        return false;
                    });
                    this.changeDetectorRef.detectChanges();
                },
                error: () => {
                    this.loading = false;
                    this.changeDetectorRef.detectChanges();
                }
            });
        } else if (this.showLoadingUntil === ChartLoadingOptionsEnum.any) {
            this.loading = true;
            this.loadingSubscriber = combineLatest(seriesProviders).subscribe({
                next: (data) => {
                    this.loading = true;
                    data.find((item) => {
                        if (item) {
                            this.loading = false;
                            return true;
                        }
                        return false;
                    });
                    this.changeDetectorRef.detectChanges();
                },
                error: () => {
                    this.loading = false;
                    this.changeDetectorRef.detectChanges();
                }
            });
        } else {
            this.loading = false;
        }
    }

    /**
     * Stops watching the loading state of series data
     */
    public unwatchLoading(): void {
        this.loading = false;
        if (this.loadingSubscriber) {
            this.loadingSubscriber.unsubscribe();
        }
    }
}
